import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Instagram, Music2 } from 'lucide-react';
import DOMPurify from "dompurify";

/**
 * Component to render Instagram and TikTok embeds in posts
 */
export function SocialEmbed({ embed }) {
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!embed || !embed.html) return;

    // For TikTok, we need to load their embed script and trigger re-processing
    if (embed.platform === 'tiktok' && embed.html) {
      // Check if TikTok script is already loaded
      const existingScript = document.querySelector('script[src*="tiktok.com/embed.js"]');
      
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        script.onload = () => {
          // Process embeds after script loads
          setTimeout(() => {
            if (window.tiktokEmbed && window.tiktokEmbed.lib) {
              window.tiktokEmbed.lib.render();
            }
          }, 100);
        };
        document.body.appendChild(script);
      } else {
        // Re-process embeds if script already exists
        // Use a small delay to ensure DOM is updated
        setTimeout(() => {
          if (window.tiktokEmbed && window.tiktokEmbed.lib) {
            window.tiktokEmbed.lib.render();
          }
        }, 100);
      }
    }

    setLoaded(true);
  }, [embed]);

  if (!embed) return null;

  if (embed.error) {
    return (
      <div className="bg-[#F9F7F2] rounded-lg p-4 border border-[#E5E0D5]">
        <div className="flex items-center gap-2 text-[#6B665E]">
          {embed.platform === 'instagram' ? (
            <Instagram className="w-5 h-5" />
          ) : (
            <Music2 className="w-5 h-5" />
          )}
          <span className="text-sm font-['Nunito']">
            Could not load {embed.platform} embed
          </span>
        </div>
        <a 
          href={embed.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-[var(--liturgical-primary)] hover:underline mt-2 flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" />
          View on {embed.platform}
        </a>
      </div>
    );
  }

  // Render TikTok embed (iframe-based for reliable video playback)
  if (embed.platform === 'tiktok' && embed.html) {
    // Clean the HTML - remove script tags if any
    const cleanHtml = embed.html.replace(/<script[^>]*>.*?<\/script>/gi, '');
    
    return (
      <div className="social-embed tiktok-embed my-3" data-testid="tiktok-embed">
        <div 
          ref={containerRef}
          className="max-w-[325px] mx-auto rounded-lg overflow-hidden"
          style={{ minHeight: '575px' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanHtml) }}
        />
        {embed.author_name && (
          <p className="text-xs text-[#6B665E] font-['Nunito'] mt-2 text-center">
            @{embed.author_name} on TikTok
          </p>
        )}
      </div>
    );
  }

  // Render Instagram embed (iframe)
  if (embed.platform === 'instagram' && embed.html) {
    return (
      <div className="social-embed instagram-embed my-3" data-testid="instagram-embed">
        <div 
          ref={containerRef}
          className="max-w-[400px] mx-auto overflow-hidden rounded-lg"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(embed.html) }}
        />
      </div>
    );
  }

  // Fallback - just show a link
  return (
    <a 
      href={embed.url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5] hover:bg-[#F0EBE0] transition-colors my-2"
    >
      {embed.platform === 'instagram' ? (
        <Instagram className="w-5 h-5 text-[#E4405F]" />
      ) : (
        <Music2 className="w-5 h-5 text-[#000]" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-['Nunito'] text-[#2D2A26] truncate">
          {embed.title || `View on ${embed.platform}`}
        </p>
        {embed.author_name && (
          <p className="text-xs text-[#6B665E]">@{embed.author_name}</p>
        )}
      </div>
      <ExternalLink className="w-4 h-4 text-[#6B665E]" />
    </a>
  );
}

/**
 * Component to render multiple embeds
 */
export function SocialEmbeds({ embeds }) {
  if (!embeds || embeds.length === 0) return null;

  return (
    <div className="social-embeds space-y-3">
      {embeds.map((embed, index) => (
        <SocialEmbed key={`${embed.platform}-${index}`} embed={embed} />
      ))}
    </div>
  );
}

export default SocialEmbed;
